import { motion } from 'framer-motion';
import { useCallback, useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import AuthAxios from '../../axios';
import FallBackImg from '../../assets/images/fallback.jpg';
import { AuthStateContext } from '../../contexts/AuthContext';
import { MusicPlayerStateContext } from '../../contexts/MusicContext';
import { useLgUp } from '../../hooks/useMediaQuery';
import { Track } from '../../types';
import { transLn } from '../../utils/transLn';
import { MUSIC_RESUME_TRACK_KEY } from '../../utils/playlist/musicResumeSession';
import { playlistFromApiResponse } from '../../utils/playlist/playlistQueueOrder';
import { getTrackImageUrl } from '../../utils/trackImageUrl';
import { Icon } from '../header/draft/Icon';
import { GlassSurface } from '../profile/GlassSurface';
import '../../components/profile/profile-glass.css';
import { MiniPlayerEqualizer } from './MiniPlayerEqualizer';
import { MiniPlayerScrollingTitle } from './MiniPlayerScrollingTitle';
import { MiniPlayerVolumeControl } from './MiniPlayerVolumeControl';
import './mini-player.css';

const imgBase = process.env.REACT_APP_IMG_ENDPOINT;

/** Draft MiniPlayer 레이아웃 + 실서버 MusicContext */
export default function MiniPlayer() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const path = (location.state as { path?: string } | null)?.path;
  const lgUp = useLgUp();

  const { isAuthentication, user } = useContext(AuthStateContext);
  const {
    audioRef,
    currentPlayMusic,
    isReady,
    isMusicPlaying,
    volume,
    duration,
    currentProgress,
    togglePlayPause,
    handleVolumeChange,
    onChangeCurrentProgressBar,
    onChangeMusic,
  } = useContext(MusicPlayerStateContext);

  const [isHidden, setIsHidden] = useState(false);
  const [spot, setSpot] = useState({ x: 50, y: 50, visible: false });
  const [playlistTracks, setPlaylistTracks] = useState<Track[]>([]);

  const progressPercent = duration > 0 ? Math.min(100, (currentProgress / duration) * 100) : 0;
  const title = currentPlayMusic ? transLn(currentPlayMusic.title) : '';
  const category = currentPlayMusic ? transLn(currentPlayMusic.category) : '';
  const thumbSrc = currentPlayMusic ? getTrackImageUrl(currentPlayMusic) : '';

  const handleBarPointerMove = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      const rect = e.currentTarget.getBoundingClientRect();
      setSpot({
        x: ((e.clientX - rect.left) / rect.width) * 100,
        y: ((e.clientY - rect.top) / rect.height) * 100,
        visible: true,
      });
    },
    [],
  );

  const handleBarPointerLeave = useCallback(() => {
    setSpot((s) => ({ ...s, visible: false }));
  }, []);

  const openPlayer = useCallback(() => {
    if (!currentPlayMusic) return;
    if (
      window.location.pathname.includes('/app/home/comment/') ||
      window.location.pathname.includes('/app/home/playlist')
    ) {
      navigate(path ?? '/app/home');
      return;
    }
    navigate(`/app/player/${currentPlayMusic.id}`, {
      state: { path: window.location.pathname },
    });
  }, [currentPlayMusic, navigate, path]);

  const handleProgressSeek = useCallback(
    (e: React.MouseEvent<HTMLDivElement> | React.PointerEvent<HTMLDivElement>) => {
      e.stopPropagation();
      if (!audioRef?.current || duration <= 0) return;
      const rect = e.currentTarget.getBoundingClientRect();
      const ratio = Math.min(1, Math.max(0, (e.clientX - rect.left) / rect.width));
      const nextTime = ratio * duration;
      audioRef.current.currentTime = nextTime;
      onChangeCurrentProgressBar(nextTime);
    },
    [audioRef, duration, onChangeCurrentProgressBar],
  );

  useEffect(() => {
    setIsHidden(window.location.pathname.includes('/app/player/'));
  }, [location.pathname]);

  useEffect(() => {
    if (!isAuthentication) return;
    AuthAxios.get<{ list: Track[] }>('/playlist')
      .then((response) => setPlaylistTracks(playlistFromApiResponse(response?.data?.list)))
      .catch(() => setPlaylistTracks([]));
  }, [isAuthentication]);

  useEffect(() => {
    if (!currentPlayMusic && isAuthentication && user?.currentSubscription === true) {
      if (window.sessionStorage.getItem(MUSIC_RESUME_TRACK_KEY)) return;
      if (window.location.pathname.includes('/app/player/')) return;
      if (playlistTracks[0]) {
        onChangeMusic(playlistTracks[0]);
      }
    }
  }, [playlistTracks, currentPlayMusic, isAuthentication, user?.currentSubscription, onChangeMusic]);

  if (currentPlayMusic === null || isHidden) return null;

  return (
    <div
      className="soundpill-mini-player--with-sidebar fixed bottom-5 z-[60] inset-x-0 flex justify-center pointer-events-none px-4 sm:px-6"
      aria-label={t('miniPlayer.label')}
    >
      <motion.div
        initial={{ y: 40, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ type: 'spring', stiffness: 120, damping: 18 }}
        className="pointer-events-auto w-full max-w-[640px] lg:max-w-[480px]"
      >
        <motion.div
          className="soundpill-mini-player-bar-wrap relative w-full overflow-hidden lg:overflow-visible lg:cursor-pointer"
          whileHover={lgUp ? { scale: 1.025 } : undefined}
          transition={{ type: 'spring', stiffness: 380, damping: 26 }}
        >
          <GlassSurface
            rounded="full"
            className={`soundpill-mini-player-bar mini-player-bar relative w-full shadow-[0_8px_40px_rgba(0,0,0,0.45)]${
              isMusicPlaying ? ' is-playing' : ''
            }`}
            style={
              {
                '--spot-x': `${spot.x}%`,
                '--spot-y': `${spot.y}%`,
                '--spot-opacity': spot.visible && lgUp ? '1' : '0',
              } as React.CSSProperties
            }
            onPointerMove={lgUp ? handleBarPointerMove : undefined}
            onPointerLeave={lgUp ? handleBarPointerLeave : undefined}
          >
            <div className="soundpill-mini-player-aura" aria-hidden>
              <div className="soundpill-mini-player-aura__sweep" />
              <div className="soundpill-mini-player-aura__orb soundpill-mini-player-aura__orb--primary" />
              <div className="soundpill-mini-player-aura__orb soundpill-mini-player-aura__orb--cyan" />
              <div className="soundpill-mini-player-aura__orb soundpill-mini-player-aura__orb--violet" />
            </div>
            <div className="soundpill-mini-player-spotlight" aria-hidden />
            <div className="relative z-[2] flex items-center px-2.5 sm:px-3 gap-2 sm:gap-2.5 h-[72px] sm:h-20">
              <button
                type="button"
                onClick={openPlayer}
                className="flex items-center gap-2.5 sm:gap-3 min-w-0 flex-1 text-left lg:cursor-pointer"
              >
                <div
                  className={`soundpill-mini-player-thumb w-14 h-14 sm:w-16 sm:h-16 rounded-full overflow-hidden shrink-0 bg-[#2e2d36]${
                    isMusicPlaying ? ' is-playing' : ''
                  }`}
                >
                  <img
                    src={thumbSrc || FallBackImg}
                    alt=""
                    className="soundpill-mini-player-thumb__img"
                    onError={(e) => {
                      e.currentTarget.src = FallBackImg;
                    }}
                    loading="lazy"
                  />
                  {isMusicPlaying && (
                    <div className="soundpill-mini-player-thumb__eq-overlay">
                      <MiniPlayerEqualizer />
                    </div>
                  )}
                </div>
                <div className="flex flex-col min-w-0 flex-1 gap-0.5">
                  <MiniPlayerScrollingTitle text={title} />
                  <span className="text-xs text-on-surface-variant/80 truncate">{category}</span>
                </div>
              </button>

              <div className="flex items-center gap-1.5 sm:gap-2 shrink-0">
                <MiniPlayerVolumeControl volume={volume} onVolumeChange={handleVolumeChange} />
                <button
                  type="button"
                  disabled={!isReady}
                  onClick={(e) => {
                    e.stopPropagation();
                    togglePlayPause();
                  }}
                  className="soundpill-mini-player-play-btn w-12 h-12 sm:w-14 sm:h-14 rounded-full bg-white flex items-center justify-center shadow-[0_2px_14px_rgba(0,0,0,0.28)] lg:hover:scale-105 lg:cursor-pointer transition-transform shrink-0 disabled:opacity-50 disabled:cursor-not-allowed"
                  aria-label={isMusicPlaying ? t('miniPlayer.pause') : t('miniPlayer.play')}
                  aria-pressed={isMusicPlaying}
                >
                  <Icon
                    name={isMusicPlaying ? 'pause' : 'play_arrow'}
                    filled
                    className="soundpill-mini-player-play-btn__icon text-2xl sm:text-[1.75rem] text-black"
                  />
                </button>
              </div>
            </div>

            <div
              className="soundpill-mini-player-progress mini-player-progress"
              role="slider"
              tabIndex={0}
              aria-valuenow={Math.round(progressPercent)}
              aria-valuemin={0}
              aria-valuemax={100}
              aria-label={t('miniPlayer.playbackProgress')}
              onClick={handleProgressSeek}
              onPointerDown={handleProgressSeek}
              onKeyDown={(e) => {
                if (!audioRef?.current || duration <= 0) return;
                const step = duration * 0.05;
                if (e.key === 'ArrowRight') {
                  const next = Math.min(duration, currentProgress + step);
                  audioRef.current.currentTime = next;
                  onChangeCurrentProgressBar(next);
                } else if (e.key === 'ArrowLeft') {
                  const next = Math.max(0, currentProgress - step);
                  audioRef.current.currentTime = next;
                  onChangeCurrentProgressBar(next);
                }
              }}
            >
              <div className="soundpill-mini-player-progress__inner">
                <motion.div
                  className="soundpill-mini-player-progress-fill mini-player-progress-fill bg-primary"
                  initial={false}
                  animate={{ width: `${progressPercent}%` }}
                  transition={{ duration: 0.15, ease: 'easeOut' }}
                />
              </div>
            </div>
          </GlassSurface>
        </motion.div>
      </motion.div>
    </div>
  );
}
